import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/api';
import { toast } from 'sonner';
import { ArrowLeft, Save, FileText, Plus, Trash2, Loader2, Info } from 'lucide-react';

const SI_DISCIPLINES = [
  'Architectural', 'Structural', 'Electrical', 'Mechanical', 'Wet Services',
  'Fire', 'Lifts', 'Civil', 'Interior', 'Landscaping', 'General', 'Other'
];

const SI_IMPACT_OPTIONS = ['Yes', 'No', 'Unknown'];

export default function SICreate() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showStartingPrompt, setShowStartingPrompt] = useState(false);
  const [startingNumber, setStartingNumber] = useState('1');
  
  const [formData, setFormData] = useState({
    si_number: '',
    si_title: '',
    si_description: '',
    issued_by_consultant_id: '',
    issued_by_person_name: '',
    issued_to: '',
    issue_date: '',
    received_date: '',
    location_area: '',
    discipline: 'General',
    site_instruction_book_ref: '',
    cost_impact_expected: 'Unknown',
    time_impact_expected: 'Unknown',
    status: 'Draft'
  });
  
  const [drawingsReferenced, setDrawingsReferenced] = useState([]);

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      const [projectRes, teamRes, numbersRes] = await Promise.all([
        api.projects.getById(projectId),
        api.professionalTeam.getAll(projectId).catch(() => ({ data: [] })),
        api.get(`/projects/${projectId}/starting-numbers`).catch(() => ({ data: {} }))
      ]);
      setProject(projectRes.data.project);
      setConsultants(teamRes.data);
      
      const numbers = numbersRes.data;
      if (!numbers.si_configured && numbers.si_count === 0) {
        setShowStartingPrompt(true);
      } else {
        const nextNum = (numbers.si_starting_number || 1) + (numbers.si_count || 0);
        setFormData(prev => ({ ...prev, si_number: `SI-${String(nextNum).padStart(3, '0')}` }));
      }
    } catch (error) {
      toast.error('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmStarting = async () => {
    const num = parseInt(startingNumber);
    if (isNaN(num) || num < 1) {
      toast.error('Please enter a valid starting number');
      return;
    }
    try {
      await api.post(`/projects/${projectId}/starting-numbers`, { si_starting_number: num });
      setFormData(prev => ({ ...prev, si_number: `SI-${String(num).padStart(3, '0')}` }));
      setShowStartingPrompt(false);
      toast.success(`SI numbering will start from SI-${String(num).padStart(3, '0')}`);
    } catch {
      toast.error('Failed to save starting number');
    }
  };

  const handleSubmit = async (e, saveAsCaptured = false) => {
    e.preventDefault();
    
    // Validation
    if (!formData.si_number.trim()) {
      toast.error('SI Number is required');
      return;
    }
    if (!formData.si_title.trim()) {
      toast.error('SI Title is required');
      return;
    }
    if (!formData.issued_by_consultant_id) {
      toast.error('Issuing Consultant is required');
      return;
    }
    
    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        drawings_referenced: drawingsReferenced.filter(d => d.drawing_number),
        status: saveAsCaptured ? 'Captured' : 'Draft'
      };
      
      const response = await api.siteInstructions.create(projectId, submitData);
      toast.success(`Site Instruction ${formData.si_number} created`);
      navigate(`/projects/${projectId}/si/${response.data.id}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create Site Instruction';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const addDrawingReference = () => {
    setDrawingsReferenced([...drawingsReferenced, { drawing_number: '', revision: '', description: '' }]);
  };

  const updateDrawingReference = (index, field, value) => {
    const updated = [...drawingsReferenced];
    updated[index][field] = value;
    setDrawingsReferenced(updated);
  };

  const removeDrawingReference = (index) => {
    setDrawingsReferenced(drawingsReferenced.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Starting Number Prompt */}
        {showStartingPrompt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Info className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">Set Starting SI Number</h3>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                This is the first Site Instruction for this project. If you're joining mid-project, enter the number your SIs should start from.
              </p>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-sm font-medium text-slate-700 whitespace-nowrap">SI-</span>
                <Input
                  type="number"
                  min="1"
                  value={startingNumber}
                  onChange={(e) => setStartingNumber(e.target.value)}
                  className="w-32"
                  placeholder="1"
                  data-testid="si-starting-number-input"
                  autoFocus
                />
                <span className="text-xs text-slate-500">First SI will be SI-{String(parseInt(startingNumber) || 1).padStart(3, '0')}</span>
              </div>
              <div className="flex gap-3">
                <Button onClick={handleConfirmStarting} className="flex-1 bg-slate-900 text-white hover:bg-slate-800" data-testid="si-starting-confirm">
                  Confirm
                </Button>
                <Button variant="outline" onClick={() => { setShowStartingPrompt(false); setFormData(prev => ({ ...prev, si_number: 'SI-001' })); }} className="flex-1" data-testid="si-starting-skip">
                  Start from 1
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate(`/projects/${projectId}#si`)}
            variant="ghost"
            className="text-slate-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-barlow font-bold uppercase">New Site Instruction</h1>
            <p className="text-sm text-slate-500 mt-1">{project?.name}</p>
          </div>
        </div>

        <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-8">
          {/* SI Number & Title */}
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <h2 className="text-lg font-barlow font-bold uppercase text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Site Instruction Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  SI Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.si_number}
                  onChange={(e) => setFormData({ ...formData, si_number: e.target.value })}
                  placeholder="e.g., SI-001, A/SI/2026/001"
                  required
                  className="rounded-sm font-mono"
                  data-testid="si-number-input"
                />
                <p className="text-xs text-slate-400 mt-1">Enter exactly as per SI book</p>
              </div>
              
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  Discipline
                </Label>
                <Select 
                  value={formData.discipline} 
                  onValueChange={(value) => setFormData({ ...formData, discipline: value })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="discipline-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SI_DISCIPLINES.map(disc => (
                      <SelectItem key={disc} value={disc}>{disc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="mt-6">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                SI Title <span className="text-red-500">*</span>
              </Label>
              <Input
                value={formData.si_title}
                onChange={(e) => setFormData({ ...formData, si_title: e.target.value })}
                placeholder="Brief description of the instruction"
                required
                className="rounded-sm"
                data-testid="si-title-input"
              />
            </div>
            
            <div className="mt-6">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                Full Instruction Description
              </Label>
              <Textarea
                value={formData.si_description}
                onChange={(e) => setFormData({ ...formData, si_description: e.target.value })}
                placeholder="Enter the full instruction text..."
                rows={5}
                className="rounded-sm resize-none"
                data-testid="si-description-input"
              />
            </div>
          </div>

          {/* Issuing Consultant */}
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <h2 className="text-lg font-barlow font-bold uppercase text-slate-900 mb-4">
              Issuing Consultant
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  Consultant / Company <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={formData.issued_by_consultant_id} 
                  onValueChange={(value) => {
                    // Find the selected consultant and auto-populate person name
                    const selectedConsultant = consultants.find(c => c.id === value);
                    const personName = selectedConsultant?.contact_name || 
                      (selectedConsultant?.primary_contact?.name && selectedConsultant?.primary_contact?.surname 
                        ? `${selectedConsultant.primary_contact.name} ${selectedConsultant.primary_contact.surname}`
                        : formData.issued_by_person_name);
                    setFormData({ 
                      ...formData, 
                      issued_by_consultant_id: value,
                      issued_by_person_name: personName
                    });
                  }}
                >
                  <SelectTrigger className="rounded-sm" data-testid="consultant-select">
                    <SelectValue placeholder="Select consultant" />
                  </SelectTrigger>
                  <SelectContent>
                    {consultants.map(c => {
                      const contactName = c.primary_contact 
                        ? `${c.primary_contact.name || ''} ${c.primary_contact.surname || ''}`.trim()
                        : '';
                      const displayName = c.company_name || contactName || 'Unknown';
                      return (
                        <SelectItem key={c.id} value={c.id}>
                          {displayName} - {c.discipline}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {consultants.length === 0 && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800 font-medium">
                      No consultants found in Professional Team
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      Before creating a Site Instruction, you need to add consultants to this project's Professional Team.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2 text-amber-700 border-amber-300 hover:bg-amber-100"
                      onClick={() => navigate(`/projects/${projectId}#team`)}
                    >
                      Go to Project Team Settings
                    </Button>
                  </div>
                )}
              </div>
              
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  Person Name (if known)
                </Label>
                <Input
                  value={formData.issued_by_person_name}
                  onChange={(e) => setFormData({ ...formData, issued_by_person_name: e.target.value })}
                  placeholder="Name of person who signed SI"
                  className="rounded-sm"
                />
              </div>
            </div>
            
            <div className="mt-6">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                Issued To (Contractor/Team)
              </Label>
              <Input
                value={formData.issued_to}
                onChange={(e) => setFormData({ ...formData, issued_to: e.target.value })}
                placeholder="e.g., Main Contractor, Electrical Subcontractor"
                className="rounded-sm"
              />
            </div>
          </div>

          {/* Dates */}
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <h2 className="text-lg font-barlow font-bold uppercase text-slate-900 mb-4">
              Dates & Location
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  Issue Date
                </Label>
                <Input
                  type="date"
                  value={formData.issue_date}
                  onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                  className="rounded-sm"
                  data-testid="issue-date-input"
                />
              </div>
              
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  Received Date
                </Label>
                <Input
                  type="date"
                  value={formData.received_date}
                  onChange={(e) => setFormData({ ...formData, received_date: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  SI Book Reference
                </Label>
                <Input
                  value={formData.site_instruction_book_ref}
                  onChange={(e) => setFormData({ ...formData, site_instruction_book_ref: e.target.value })}
                  placeholder="e.g., Page 45"
                  className="rounded-sm"
                />
              </div>
            </div>
            
            <div className="mt-6">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                Location / Area
              </Label>
              <Input
                value={formData.location_area}
                onChange={(e) => setFormData({ ...formData, location_area: e.target.value })}
                placeholder="e.g., Building A, Level 3, Zone B"
                className="rounded-sm"
              />
            </div>
          </div>

          {/* Drawings Referenced */}
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-barlow font-bold uppercase text-slate-900">
                Drawings Referenced
              </h2>
              <Button
                type="button"
                onClick={addDrawingReference}
                variant="outline"
                size="sm"
                className="rounded-sm"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Drawing
              </Button>
            </div>
            
            {drawingsReferenced.length === 0 ? (
              <p className="text-sm text-slate-400">No drawings referenced yet</p>
            ) : (
              <div className="space-y-3">
                {drawingsReferenced.map((drawing, idx) => (
                  <div key={idx} className="flex gap-3 items-start p-3 bg-slate-50 rounded-sm">
                    <div className="flex-1">
                      <Input
                        value={drawing.drawing_number}
                        onChange={(e) => updateDrawingReference(idx, 'drawing_number', e.target.value)}
                        placeholder="Drawing Number"
                        className="rounded-sm mb-2"
                      />
                      <div className="flex gap-2">
                        <Input
                          value={drawing.revision}
                          onChange={(e) => updateDrawingReference(idx, 'revision', e.target.value)}
                          placeholder="Rev"
                          className="rounded-sm w-20"
                        />
                        <Input
                          value={drawing.description}
                          onChange={(e) => updateDrawingReference(idx, 'description', e.target.value)}
                          placeholder="Description (optional)"
                          className="rounded-sm flex-1"
                        />
                      </div>
                    </div>
                    <Button
                      type="button"
                      onClick={() => removeDrawingReference(idx)}
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Impact Assessment */}
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <h2 className="text-lg font-barlow font-bold uppercase text-slate-900 mb-4">
              Impact Assessment
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  Cost Impact Expected?
                </Label>
                <Select 
                  value={formData.cost_impact_expected} 
                  onValueChange={(value) => setFormData({ ...formData, cost_impact_expected: value })}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SI_IMPACT_OPTIONS.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  Time Impact Expected?
                </Label>
                <Select 
                  value={formData.time_impact_expected} 
                  onValueChange={(value) => setFormData({ ...formData, time_impact_expected: value })}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SI_IMPACT_OPTIONS.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              onClick={() => navigate(`/projects/${projectId}#si`)}
              variant="outline"
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              variant="outline"
              className="rounded-sm"
              data-testid="save-draft-button"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save as Draft
            </Button>
            <Button
              type="button"
              onClick={(e) => handleSubmit(e, true)}
              disabled={submitting}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
              data-testid="save-captured-button"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save as Captured
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
